import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { 
    Camera,
    Upload,
    Loader2,
    Sparkles,
    Eye,
    Clock,
    Trash2,
    TrendingUp,
    TrendingDown,
    Minus,
    AlertTriangle,
    CheckCircle2,
    Image as ImageIcon,
    X,
    ZoomIn,
    ChevronRight,
    Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BODY_AREAS = [
    { value: 'face', label: 'Face' },
    { value: 'scalp', label: 'Scalp' },
    { value: 'neck', label: 'Neck' },
    { value: 'chest', label: 'Chest' },
    { value: 'back', label: 'Back' },
    { value: 'abdomen', label: 'Abdomen' },
    { value: 'arms', label: 'Arms' },
    { value: 'hands', label: 'Hands' },
    { value: 'legs', label: 'Legs' },
    { value: 'feet', label: 'Feet' },
    { value: 'joints', label: 'Joints' },
    { value: 'other', label: 'Other' },
];

export default function PhotoLog() {
    const { token } = useAuth();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('capture');
    const fileInputRef = useRef(null);
    
    // Capture state
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [bodyArea, setBodyArea] = useState('other');
    const [notes, setNotes] = useState('');
    
    // Analysis state
    const [analyzing, setAnalyzing] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [saving, setSaving] = useState(false);
    
    // View state
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [entryImage, setEntryImage] = useState(null);
    const [loadingImage, setLoadingImage] = useState(false);
    
    // Timeline state
    const [timelineArea, setTimelineArea] = useState('');
    const [timeline, setTimeline] = useState(null);

    const fetchEntries = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/photo-log?limit=50`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setEntries(data || []);
            }
        } catch (error) {
            console.error('Failed to fetch entries:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    const handleImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                toast.error('Image too large. Maximum 10MB.');
                return;
            }
            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
            setAnalysis(null);
        }
    };

    const analyzeImage = async () => {
        if (!selectedImage) return;
        
        setAnalyzing(true);
        try {
            const formData = new FormData();
            formData.append('image', selectedImage);
            formData.append('body_area', bodyArea);
            
            const res = await fetch(`${API_URL}/api/photo-log/analyze`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            
            if (res.ok) {
                const data = await res.json();
                setAnalysis(data);
                toast.success('Analysis complete!');
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Analysis failed');
            }
        } catch (error) {
            toast.error('Analysis failed');
        } finally {
            setAnalyzing(false);
        }
    };

    const saveEntry = async () => {
        if (!selectedImage) return;
        
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('image', selectedImage);
            formData.append('body_area', bodyArea);
            formData.append('notes', notes);
            
            if (analysis) {
                formData.append('symptoms_visible', JSON.stringify(analysis.visible_symptoms || []));
                formData.append('severity', analysis.severity_estimate || '');
                formData.append('description', analysis.description || '');
                formData.append('characteristics', JSON.stringify(analysis.characteristics || {}));
                formData.append('changes_to_monitor', JSON.stringify(analysis.changes_to_monitor || []));
            }
            
            const res = await fetch(`${API_URL}/api/photo-log`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            
            if (res.ok) {
                toast.success('Photo log saved!');
                resetCapture();
                fetchEntries();
                setActiveTab('history');
            } else {
                toast.error('Failed to save');
            }
        } catch (error) {
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const resetCapture = () => {
        setSelectedImage(null);
        setImagePreview(null);
        setAnalysis(null);
        setNotes('');
        setBodyArea('other');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const viewEntry = async (entry) => {
        setSelectedEntry(entry);
        setLoadingImage(true);
        
        try {
            const res = await fetch(`${API_URL}/api/photo-log/${entry.id}/image`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const blob = await res.blob();
                setEntryImage(URL.createObjectURL(blob));
            }
        } catch (error) {
            console.error('Failed to load image:', error);
        } finally {
            setLoadingImage(false);
        }
    };

    const deleteEntry = async (entryId) => {
        try {
            const res = await fetch(`${API_URL}/api/photo-log/${entryId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                toast.success('Entry deleted');
                fetchEntries();
                setSelectedEntry(null);
            }
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const fetchTimeline = async (area) => {
        if (!area) return;
        
        try {
            const res = await fetch(`${API_URL}/api/photo-log/timeline/${area}?limit=10`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                setTimeline(data);
            }
        } catch (error) {
            console.error('Failed to fetch timeline:', error);
        }
    };

    useEffect(() => {
        if (timelineArea) {
            fetchTimeline(timelineArea);
        }
    }, [timelineArea]);

    const getTrendIcon = (trend) => {
        switch (trend) {
            case 'improving': return <TrendingDown className="w-5 h-5 text-emerald-500" />;
            case 'worsening': return <TrendingUp className="w-5 h-5 text-red-500" />;
            case 'stable': return <Minus className="w-5 h-5 text-blue-500" />;
            default: return null;
        }
    };

    const getSeverityColor = (severity) => {
        if (!severity) return 'bg-gray-100 text-gray-600';
        if (severity >= 7) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
        if (severity >= 4) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                    <Camera className="w-8 h-8 text-violet-500" />
                    Symptom Photo Log
                </h1>
                <p className="text-muted-foreground mt-1">Document visible symptoms with AI analysis to track changes over time</p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="capture">
                        <Camera className="w-4 h-4 mr-2" />
                        Capture
                    </TabsTrigger>
                    <TabsTrigger value="history">
                        <Clock className="w-4 h-4 mr-2" />
                        History
                    </TabsTrigger>
                    <TabsTrigger value="timeline">
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Timeline
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="capture" className="mt-6 space-y-6">
                    {/* Upload Area */}
                    <Card className="border-2 border-dashed border-violet-200 dark:border-violet-800">
                        <CardContent className="p-6">
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/*"
                                capture="environment"
                                onChange={handleImageSelect}
                                className="hidden"
                                data-testid="photo-input"
                            />
                            
                            {!imagePreview ? (
                                <div 
                                    className="flex flex-col items-center justify-center py-12 cursor-pointer"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mb-4 shadow-lg shadow-violet-500/20">
                                        <Camera className="w-10 h-10 text-white" />
                                    </div>
                                    <p className="font-medium text-lg">Take or Upload Photo</p>
                                    <p className="text-sm text-muted-foreground mt-1">Tap to capture a symptom photo</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="relative">
                                        <img 
                                            src={imagePreview} 
                                            alt="Selected symptom" 
                                            className="w-full max-h-80 object-contain rounded-lg"
                                        />
                                        <Button
                                            variant="secondary"
                                            size="icon"
                                            className="absolute top-2 right-2"
                                            onClick={resetCapture}
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Body Area</Label>
                                            <Select value={bodyArea} onValueChange={setBodyArea}>
                                                <SelectTrigger data-testid="body-area-select">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {BODY_AREAS.map(area => (
                                                        <SelectItem key={area.value} value={area.value}>
                                                            {area.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Notes (optional)</Label>
                                            <Textarea
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                placeholder="Any additional observations..."
                                                rows={2}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="flex gap-3">
                                        <Button
                                            onClick={analyzeImage}
                                            disabled={analyzing}
                                            className="gap-2"
                                            data-testid="analyze-button"
                                        >
                                            {analyzing ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Sparkles className="w-4 h-4" />
                                            )}
                                            {analyzing ? 'Analyzing...' : 'Analyze with AI'}
                                        </Button>
                                        <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                                            <Upload className="w-4 h-4 mr-2" />
                                            Change Photo
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Analysis Results */}
                    {analysis && (
                        <Card className="border-violet-200 dark:border-violet-800">
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Eye className="w-5 h-5 text-violet-500" />
                                    AI Analysis
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Description */}
                                {analysis.description && (
                                    <div className="p-4 rounded-xl bg-violet-50 dark:bg-violet-950/30">
                                        <p className="text-sm">{analysis.description}</p>
                                    </div>
                                )}

                                <div className="grid md:grid-cols-2 gap-6">
                                    {/* Visible Symptoms */}
                                    <div className="space-y-2">
                                        <p className="font-medium flex items-center gap-2">
                                            <Activity className="w-4 h-4 text-rose-500" />
                                            Visible Symptoms
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {analysis.visible_symptoms?.length > 0 ? (
                                                analysis.visible_symptoms.map((sym, idx) => (
                                                    <Badge key={idx} variant="outline" className="border-rose-300 text-rose-700">
                                                        {sym}
                                                    </Badge>
                                                ))
                                            ) : (
                                                <span className="text-sm text-muted-foreground">None detected</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Severity */}
                                    {analysis.severity_estimate && (
                                        <div className="space-y-2">
                                            <p className="font-medium">Severity Estimate</p>
                                            <Badge className={getSeverityColor(analysis.severity_estimate)}>
                                                {analysis.severity_estimate}/10
                                            </Badge>
                                        </div>
                                    )}
                                </div>

                                {/* Characteristics */}
                                {analysis.characteristics && Object.keys(analysis.characteristics).length > 0 && (
                                    <div className="space-y-2">
                                        <p className="font-medium">Characteristics</p>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            {Object.entries(analysis.characteristics).map(([key, value]) => (
                                                value && (
                                                    <div key={key} className="p-3 rounded-lg bg-muted/50">
                                                        <p className="text-xs text-muted-foreground capitalize">{key.replace('_', ' ')}</p>
                                                        <p className="text-sm font-medium">{value}</p>
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Changes to Monitor */}
                                {analysis.changes_to_monitor?.length > 0 && (
                                    <div className="space-y-2">
                                        <p className="font-medium flex items-center gap-2">
                                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                                            Changes to Monitor
                                        </p>
                                        <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                                            {analysis.changes_to_monitor.map((item, idx) => (
                                                <li key={idx}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Recommendation */}
                                {analysis.recommendation && (
                                    <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30">
                                        <p className="font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2 mb-1">
                                            <CheckCircle2 className="w-4 h-4" />
                                            Recommendation
                                        </p>
                                        <p className="text-sm text-blue-600 dark:text-blue-400">{analysis.recommendation}</p>
                                    </div>
                                )}

                                {/* Save Button */}
                                <div className="pt-4 border-t">
                                    <Button
                                        onClick={saveEntry}
                                        disabled={saving}
                                        className="gap-2"
                                        data-testid="save-entry-button"
                                    >
                                        {saving ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Camera className="w-4 h-4" />
                                        )}
                                        {saving ? 'Saving...' : 'Save Photo Log'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Save without analysis option */}
                    {imagePreview && !analysis && (
                        <div className="text-center">
                            <Button
                                variant="outline"
                                onClick={saveEntry}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Without Analysis'}
                            </Button>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="history" className="mt-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
                        </div>
                    ) : entries.length === 0 ? (
                        <Card>
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <ImageIcon className="w-12 h-12 text-muted-foreground/50 mb-4" />
                                <h3 className="font-medium text-lg mb-2">No photos yet</h3>
                                <p className="text-muted-foreground text-center max-w-md">
                                    Start documenting your visible symptoms to track changes over time.
                                </p>
                                <Button className="mt-4" onClick={() => setActiveTab('capture')}>
                                    <Camera className="w-4 h-4 mr-2" />
                                    Take First Photo
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {entries.map((entry) => (
                                <Card 
                                    key={entry.id} 
                                    className="cursor-pointer hover:ring-2 hover:ring-violet-500 transition-all overflow-hidden"
                                    onClick={() => viewEntry(entry)}
                                    data-testid={`entry-${entry.id}`}
                                >
                                    <div className="aspect-square bg-muted flex items-center justify-center">
                                        <ImageIcon className="w-12 h-12 text-muted-foreground/30" />
                                    </div>
                                    <CardContent className="p-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <Badge variant="outline" className="text-xs">
                                                {BODY_AREAS.find(a => a.value === entry.body_area)?.label || entry.body_area}
                                            </Badge>
                                            {entry.severity && (
                                                <Badge className={`text-xs ${getSeverityColor(entry.severity)}`}>
                                                    {entry.severity}/10
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {format(parseISO(entry.created_at), 'MMM d, yyyy')}
                                        </p>
                                        {entry.symptoms_visible?.length > 0 && (
                                            <p className="text-xs mt-1 truncate">
                                                {entry.symptoms_visible.slice(0, 2).join(', ')}
                                            </p>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="timeline" className="mt-6 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Track Changes Over Time</CardTitle>
                            <CardDescription>Select a body area to see how symptoms have changed</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Select value={timelineArea} onValueChange={setTimelineArea}>
                                <SelectTrigger className="w-full md:w-64">
                                    <SelectValue placeholder="Select body area..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {BODY_AREAS.map(area => (
                                        <SelectItem key={area.value} value={area.value}>
                                            {area.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </CardContent>
                    </Card>

                    {timeline && (
                        <div className="space-y-4">
                            {/* Trend Summary */}
                            <Card className={`
                                ${timeline.trend === 'improving' ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20' : ''}
                                ${timeline.trend === 'worsening' ? 'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20' : ''}
                                ${timeline.trend === 'stable' ? 'border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20' : ''}
                            `}>
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        {getTrendIcon(timeline.trend)}
                                        <div>
                                            <p className="font-medium capitalize">
                                                {timeline.trend === 'insufficient_data' 
                                                    ? 'More Photos Needed' 
                                                    : `Trend: ${timeline.trend}`}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {timeline.total_entries} photos logged for {BODY_AREAS.find(a => a.value === timelineArea)?.label}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Timeline entries */}
                            {timeline.entries?.length > 0 ? (
                                <div className="space-y-3">
                                    {timeline.entries.map((entry, idx) => (
                                        <Card key={entry.id} className="cursor-pointer hover:bg-muted/50" onClick={() => viewEntry(entry)}>
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                                            <ImageIcon className="w-5 h-5 text-violet-500" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-sm">
                                                                {format(parseISO(entry.created_at), 'MMMM d, yyyy')}
                                                            </p>
                                                            {entry.symptoms_visible?.length > 0 && (
                                                                <p className="text-xs text-muted-foreground">
                                                                    {entry.symptoms_visible.slice(0, 3).join(', ')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {entry.severity && (
                                                            <Badge className={getSeverityColor(entry.severity)}>
                                                                {entry.severity}/10
                                                            </Badge>
                                                        )}
                                                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <Card>
                                    <CardContent className="py-8 text-center text-muted-foreground">
                                        No photos logged for this body area yet.
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Entry Detail Modal */}
            {selectedEntry && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => { setSelectedEntry(null); setEntryImage(null); }}>
                    <Card className="w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>
                                        {BODY_AREAS.find(a => a.value === selectedEntry.body_area)?.label || selectedEntry.body_area}
                                    </CardTitle>
                                    <CardDescription>
                                        {format(parseISO(selectedEntry.created_at), 'PPpp')}
                                    </CardDescription>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => deleteEntry(selectedEntry.id)}
                                        className="text-destructive"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => { setSelectedEntry(null); setEntryImage(null); }}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Image */}
                            <div className="aspect-video bg-muted rounded-lg flex items-center justify-center overflow-hidden">
                                {loadingImage ? (
                                    <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
                                ) : entryImage ? (
                                    <img src={entryImage} alt="Symptom" className="w-full h-full object-contain" />
                                ) : (
                                    <ImageIcon className="w-16 h-16 text-muted-foreground/30" />
                                )}
                            </div>

                            {/* Details */}
                            {selectedEntry.description && (
                                <p className="text-sm">{selectedEntry.description}</p>
                            )}

                            <div className="flex flex-wrap gap-2">
                                {selectedEntry.symptoms_visible?.map((sym, idx) => (
                                    <Badge key={idx} variant="outline">{sym}</Badge>
                                ))}
                            </div>

                            {selectedEntry.severity && (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">Severity:</span>
                                    <Badge className={getSeverityColor(selectedEntry.severity)}>
                                        {selectedEntry.severity}/10
                                    </Badge>
                                </div>
                            )}

                            {selectedEntry.notes && (
                                <div className="p-3 rounded-lg bg-muted">
                                    <p className="text-sm">{selectedEntry.notes}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
